import React, { useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import './StateList.styles.scss';

import chandigarhImage from '../../../assets/stateimages/chandigarh.jpg';
import delhiImage from '../../../assets/stateimages/delhi.jpg';
import haryanaImage from '../../../assets/stateimages/haryana.jpg';
import punjabImage from '../../../assets/stateimages/punjab.jpg';
import rajasthanImage from '../../../assets/stateimages/rajasthan.jpg';
import hpImage from '../../../assets/stateimages/hp.jpg';
import upImage from '../../../assets/stateimages/up.jpg';

// Photos we already have — keyed by display name.
const STATE_IMAGES = {
  Haryana: haryanaImage,
  Rajasthan: rajasthanImage,
  Chandigarh: chandigarhImage,
  Delhi: delhiImage,
  'Uttar Pradesh': upImage,
  'Himachal Pradesh': hpImage,
  Punjab: punjabImage,
};

// All 28 Indian states + 8 Union Territories.
const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
];

const UNION_TERRITORIES = [
  'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi',
  'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

const getInitials = (name) => {
  const parts = name.split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? parts[0][0] + parts[1][0] : name.slice(0, 2)).toUpperCase();
};

// Deterministic gradient bucket so each state keeps a stable colour.
const gradientIndex = (name) => {
  let sum = 0;
  for (let i = 0; i < name.length; i += 1) sum += name.charCodeAt(i);
  return sum % 6;
};

const StateCard = ({ name, onSelect }) => {
  const image = STATE_IMAGES[name];
  return (
    <button type="button" className="sl-card" onClick={() => onSelect(name)} title={name}>
      <span className={`sl-card__avatar sl-card__avatar--g${gradientIndex(name)}`}>
        {image
          ? <img src={image} alt={name} className="sl-card__img" loading="lazy" />
          : <span className="sl-card__initials">{getInitials(name)}</span>}
      </span>
      <span className="sl-card__name">{name}</span>
    </button>
  );
};

const StateList = ({ setCurrentSelectedState }) => {
  const [query, setQuery] = useState('');

  const onSelect = (name) =>
    setCurrentSelectedState({ category: 'state', value: name });

  const q = query.trim().toLowerCase();

  const states = useMemo(() => STATES.filter((name) => !q || name.toLowerCase().includes(q)), [q]);
  const uts = useMemo(() => UNION_TERRITORIES.filter((name) => !q || name.toLowerCase().includes(q)), [q]);
  const total = states.length + uts.length;

  return (
    <div className="sl-root">
      {/* Search */}
      <div className="sl-search">
        <SearchIcon className="sl-search__icon" fontSize="small" />
        <input
          className="sl-search__input"
          placeholder="Search state or union territory…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        {query && (
          <button className="sl-search__clear" onClick={() => setQuery('')} aria-label="Clear search">
            <CloseIcon style={{ fontSize: '1rem' }} />
          </button>
        )}
      </div>

      {total === 0 ? (
        <div className="sl-empty">
          <LocationOnOutlinedIcon />
          <p>No state or UT matches “{query}”.</p>
        </div>
      ) : (
        <div className="sl-scroll">
          {states.length > 0 && (
            <section className="sl-section">
              <p className="sl-section__label">States <span>({states.length})</span></p>
              <div className="sl-grid">
                {states.map((name) => <StateCard key={name} name={name} onSelect={onSelect} />)}
              </div>
            </section>
          )}

          {uts.length > 0 && (
            <section className="sl-section">
              <p className="sl-section__label">Union Territories <span>({uts.length})</span></p>
              <div className="sl-grid">
                {uts.map((name) => <StateCard key={name} name={name} onSelect={onSelect} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default StateList;
